import { useCallback, useEffect, useRef, useState } from 'react';
import './PhotoCarousel.css';

function PhotoCarousel({ slides = [], autoPlayInterval = 6000, showMeta = true, showDescription = true }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const [progress, setProgressState] = useState(0);

  const slidesCount = slides.length;
  const shouldPause = isUserPaused || slidesCount <= 1;
  const progressRef = useRef(progress);

  const setProgress = useCallback((value) => {
    const next = Math.min(Math.max(value, 0), 100);
    progressRef.current = next;
    setProgressState(next);
  }, []);

  const goToNext = useCallback(() => {
    if (!slidesCount) return;

    setCurrentIndex((prev) => (prev + 1) % slidesCount);
  }, [slidesCount]);

  const goToPrev = useCallback(() => {
    if (!slidesCount) return;

    setCurrentIndex((prev) => (prev - 1 + slidesCount) % slidesCount);
  }, [slidesCount]);

  useEffect(() => {
    if (!slidesCount || !autoPlayInterval || shouldPause) {
      return undefined;
    }

    let frameId;
    let startTime = performance.now() - (progressRef.current / 100) * autoPlayInterval;

    const animate = (now) => {
      const elapsed = now - startTime;
      const completed = Math.min((elapsed / autoPlayInterval) * 100, 100);
      setProgress(completed);

      if (elapsed >= autoPlayInterval) {
        setProgress(0);
        startTime = now;
        goToNext();
        return;
      }

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frameId);
  }, [autoPlayInterval, goToNext, setProgress, shouldPause, slidesCount, currentIndex]);

  useEffect(() => {
    if (currentIndex >= slidesCount) {
      setCurrentIndex(0);
    }
  }, [currentIndex, slidesCount]);

  const handleThumbnailClick = (index) => {
    setProgress(0);
    setCurrentIndex(index);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      setIsUserPaused(false);
      setProgress(0);
      goToNext();
    }

    if (event.key === 'ArrowLeft') {
      setIsUserPaused(false);
      setProgress(0);
      goToPrev();
    }
  };

  const toggleUserPause = () => {
    setIsUserPaused((prev) => !prev);
  };

  if (!slidesCount) {
    return null;
  }

  const activeSlide = slides[currentIndex];
  const playButtonLabel = isUserPaused ? '播放' : '暫停';

  return (
    <section
      className="carousel"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label=""
    >
      <div className="carousel__main" aria-live="polite">
        <div
          className="carousel__progress"
          role="progressbar"
          aria-label="Time until next photograph"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <div className="carousel__progress-fill" style={{ transform: `scaleX(${progress / 100})` }} />
        </div>
        <button
          type="button"
          className="carousel__nav carousel__nav--prev"
          onClick={() => {
            setIsUserPaused(false);
            setProgress(0);
            goToPrev();
          }}
          aria-label="View previous photograph"
        >
          <span className="carousel__nav-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" role="presentation" focusable="false">
              <path d="M15.5 4.5L8 12l7.5 7.5" />
            </svg>
          </span>
        </button>
        <div className="carousel__image-frame">
          <img
            key={activeSlide.image}
            src={activeSlide.image}
            alt={activeSlide.alt}
            className="carousel__image"
          />
          <div className="carousel__details">
            <p className="carousel__badge">{activeSlide.award}</p>
            <h3 className="carousel__title">{activeSlide.title}</h3>
            {showMeta && (activeSlide.photographer || activeSlide.location) ? (
              <p className="carousel__meta">
                {activeSlide.photographer ? <span>{activeSlide.photographer}</span> : null}
                {activeSlide.photographer && activeSlide.location ? (
                  <span className="carousel__meta-separator" aria-hidden="true">
                    {' '}
                    •
                    {' '}
                  </span>
                ) : null}
                {activeSlide.location ? <span>{activeSlide.location}</span> : null}
              </p>
            ) : null}
            {showDescription && activeSlide.description ? (
              <p className="carousel__description">{activeSlide.description}</p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="carousel__nav carousel__nav--next"
          onClick={() => {
            setIsUserPaused(false);
            setProgress(0);
            goToNext();
          }}
          aria-label="View next photograph"
        >
          <span className="carousel__nav-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" role="presentation" focusable="false">
              <path d="M8.5 4.5L16 12l-7.5 7.5" />
            </svg>
          </span>
        </button>
        <div className="carousel__controls">
          <button
            type="button"
            className="carousel__play"
            onClick={toggleUserPause}
            aria-pressed={isUserPaused}
            aria-label={`${playButtonLabel} autoplay`}
          >
            {playButtonLabel}
          </button>
          <span className="carousel__status">
            {String(currentIndex + 1).padStart(2, '0')} / {String(slidesCount).padStart(2, '0')}
          </span>
        </div>
      </div>
      <div className="carousel__thumbnails" role="list">
        {slides.map((slide, index) => {
          const isActive = index === currentIndex;

          return (
            <button
              type="button"
              key={slide.id ?? slide.image}
              className={`carousel__thumbnail${isActive ? ' is-active' : ''}`}
              onClick={() => handleThumbnailClick(index)}
              aria-label={`Show photograph ${index + 1}: ${slide.title}`}
              aria-current={isActive}
              role="listitem"
            >
              <img src={slide.thumbnail ?? slide.image} alt={slide.alt} />
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default PhotoCarousel;
